import { resolveAppUrl } from './chunk-O2TUCS6S.js';
import { asTrimmedString } from './chunk-5JRVT5VM.js';
import { ChainFamily, registry, isActiveChain, hydrateChainRegistry, isPrivateNetworkChain, getChainMetadata, chainIsEvm, isPrivateNetwork, toCanonicalChainId, dropPrivateNetworks } from './chunk-B3TPTPE2.js';
import { createLogger } from './chunk-GPJ572YS.js';
import { globalSingleton, brandClass } from './chunk-N7SZ66QO.js';
import { z } from 'zod';

// src/chains/fact-links.generated.json
var fact_links_generated_default = {
  chainlink_ccip: {
    message_id: {
      explorer: "CCIP Explorer",
      url_template: "https://ccip.chain.link/msg/{value}",
      value_pattern: "0x[0-9a-fA-F]{64}",
      via: null
    }
  },
  cowswap: {
    order_uid: {
      explorer: "CoW Explorer",
      url_template: "https://explorer.cow.fi/orders/{value}",
      value_pattern: "0x[0-9a-fA-F]{112}",
      via: null
    }
  },
  enso: {
    message_id: {
      explorer: "CCIP Explorer",
      url_template: "https://ccip.chain.link/msg/{value}",
      value_pattern: "0x[0-9a-fA-F]{64}",
      via: "chainlink_ccip"
    }
  },
  hyperlane: {
    message_id: {
      explorer: "Hyperlane Explorer",
      url_template: "https://explorer.hyperlane.xyz/message/{value}",
      value_pattern: "0x[0-9a-fA-F]{64}",
      via: null
    }
  },
  mayan: {
    vaa_id: {
      explorer: "WormholeScan",
      url_template: "https://wormholescan.io/#/tx/{value}",
      value_pattern: "[0-9]+/[0-9a-fA-F]+/[0-9]+",
      via: "wormhole"
    }
  },
  tokemak_messaging: {
    message_id: {
      explorer: "CCIP Explorer",
      url_template: "https://ccip.chain.link/msg/{value}",
      value_pattern: "0x[0-9a-fA-F]{64}",
      via: "chainlink_ccip"
    }
  },
  wormhole: {
    vaa_id: {
      explorer: "WormholeScan",
      url_template: "https://wormholescan.io/#/tx/{value}",
      value_pattern: "[0-9]+/[0-9a-fA-F]+/[0-9]+",
      via: null
    }
  }
};

// src/chains/fact-links.ts
var VALUE_TOKEN = "{value}";
function build(wire) {
  const links = {};
  const patterns = /* @__PURE__ */ new Map();
  const rejected = [];
  for (const [protocolId, facts] of Object.entries(wire)) {
    for (const [fact, row] of Object.entries(facts ?? {})) {
      const urlTemplate = asTrimmedString(row?.url_template) ?? null;
      const explorer = asTrimmedString(row?.explorer) ?? null;
      const valuePattern = asTrimmedString(row?.value_pattern) ?? null;
      if (!urlTemplate || !explorer || !urlTemplate.startsWith("https://") || !urlTemplate.includes(VALUE_TOKEN)) {
        rejected.push(`${protocolId}.${fact}`);
        continue;
      }
      let compiled = null;
      if (valuePattern) {
        try {
          compiled = new RegExp(`^(?:${valuePattern})$`);
        } catch {
          rejected.push(`${protocolId}.${fact}`);
          continue;
        }
      }
      const link = Object.freeze({
        protocolId,
        fact,
        explorer,
        urlTemplate,
        valuePattern,
        via: asTrimmedString(row?.via) ?? null
      });
      if (compiled) patterns.set(link, compiled);
      (links[protocolId] ??= {})[fact] = link;
    }
  }
  for (const facts of Object.values(links)) Object.freeze(facts);
  return { links: Object.freeze(links), patterns, rejected };
}
var BUILT = build(fact_links_generated_default);
var PROTOCOL_FACT_LINKS = BUILT.links;
function rejectedFactLinks() {
  return BUILT.rejected;
}
function getFactLink(protocolId, fact) {
  if (!protocolId || !fact) return null;
  const facts = own(PROTOCOL_FACT_LINKS, protocolId);
  return (facts && own(facts, fact)) ?? null;
}
function own(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : void 0;
}
function factLinkMatches(link, value) {
  const text = value?.trim();
  if (!text) return false;
  const pattern = BUILT.patterns.get(link);
  return pattern ? pattern.test(text) : true;
}
function factLinkHref(link, value) {
  if (!factLinkMatches(link, value)) return null;
  const encoded = value.trim().split("/").map(encodeURIComponent).join("/");
  return link.urlTemplate.replace(VALUE_TOKEN, encoded);
}
function getFactLinkUrl(protocolId, fact, value) {
  const link = getFactLink(protocolId, fact);
  return link ? factLinkHref(link, value) : null;
}
var log = createLogger("chains:effective");
var EFFECTIVE_CHAINS_TIMEOUT_MS = 1500;
var EFFECTIVE_CHAINS_BACKGROUND_TIMEOUT_MS = 15e3;
var EFFECTIVE_CHAINS_RETRY_DELAYS_MS = [1e3, 3e3, 1e4, 3e4, 6e4];
var EFFECTIVE_CHAINS_REFRESH_MS = 10 * 6e4;
var EffectiveChainsResponseSchema = z.object({
  revision: z.string().nullable().optional(),
  published_revision: z.string().nullable().optional(),
  provider_keys: z.record(z.string(), z.string()).optional(),
  chains: z.array(z.unknown())
});
var EffectiveChainsTimeout = class extends Error {
  constructor(ms) {
    super(`effective chain registry did not answer within ${ms} ms`);
    this.name = "EffectiveChainsTimeout";
  }
};
var EffectiveChainsAborted = class extends Error {
  constructor() {
    super("effective chain registry load was cancelled");
    this.name = "EffectiveChainsAborted";
  }
};
function bodyOf(payload) {
  if (payload && typeof payload === "object" && !Array.isArray(payload) && !("chains" in payload) && "data" in payload) {
    return payload.data;
  }
  return payload;
}
async function loadEffectiveChains(options) {
  const timeoutMs = Math.max(0, options.timeoutMs ?? EFFECTIVE_CHAINS_TIMEOUT_MS);
  const controller = new AbortController();
  let timer;
  let detach;
  const failed = (error) => {
    const message = error instanceof Error ? error.message : String(error);
    if (!(error instanceof EffectiveChainsAborted)) {
      log.warn("effective chain registry unavailable; keeping the registry as it is", { url: options.url, error: message });
    }
    return { ok: false, revision: null, publishedRevision: null, hydration: null, unchanged: false, error: message };
  };
  if (options.signal?.aborted) return failed(new EffectiveChainsAborted());
  try {
    const stop = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new EffectiveChainsTimeout(timeoutMs));
      }, timeoutMs);
      const signal = options.signal;
      if (signal) {
        const onAbort = () => {
          controller.abort();
          reject(new EffectiveChainsAborted());
        };
        signal.addEventListener("abort", onAbort, { once: true });
        detach = () => signal.removeEventListener("abort", onAbort);
      }
    });
    const payload = await Promise.race([
      Promise.resolve().then(() => options.fetchJson(options.url, { signal: controller.signal })),
      stop
    ]);
    if (options.signal?.aborted) return failed(new EffectiveChainsAborted());
    const parsed = EffectiveChainsResponseSchema.safeParse(bodyOf(payload));
    if (!parsed.success) return failed(new Error(`malformed effective chain registry: ${parsed.error.message}`));
    const revision = parsed.data.revision ?? null;
    const publishedRevision = parsed.data.published_revision ?? null;
    if (revision !== null && revision === options.appliedRevision) {
      return { ok: true, revision, publishedRevision, hydration: null, unchanged: true, error: null };
    }
    const hydration = hydrateChainRegistry(parsed.data.chains, { replace: true, complete: true });
    return { ok: true, revision, publishedRevision, hydration, unchanged: false, error: null };
  } catch (error) {
    return failed(error);
  } finally {
    if (timer !== void 0) clearTimeout(timer);
    detach?.();
  }
}
var running = globalSingleton("chains/effective-hydrations.v1", () => /* @__PURE__ */ new Set());
function refreshEffectiveChains() {
  for (const hydration of running) hydration.refresh();
}
function startEffectiveChainsHydration(options) {
  const retryDelays = (options.retryDelaysMs ?? EFFECTIVE_CHAINS_RETRY_DELAYS_MS).map((ms) => Math.max(0, ms));
  const refreshMs = Math.max(1e3, options.refreshMs ?? EFFECTIVE_CHAINS_REFRESH_MS);
  const timeoutMs = options.timeoutMs ?? EFFECTIVE_CHAINS_BACKGROUND_TIMEOUT_MS;
  const controller = new AbortController();
  let timer;
  let appliedRevision = null;
  let loadedOnce = false;
  let failures = 0;
  let epoch = 0;
  let inFlight = null;
  let settle = () => {
  };
  const loaded = new Promise((resolve) => {
    settle = resolve;
  });
  let self = null;
  const stop = () => {
    if (controller.signal.aborted) return;
    controller.abort();
    if (timer !== void 0) clearTimeout(timer);
    timer = void 0;
    if (self) running.delete(self);
    settle(null);
  };
  if (options.signal?.aborted) stop();
  else options.signal?.addEventListener("abort", stop, { once: true });
  const ready = async () => {
    if (!options.isReady) return true;
    try {
      return await options.isReady() === true;
    } catch {
      return false;
    }
  };
  const schedule = (ms) => {
    if (controller.signal.aborted) return;
    timer = setTimeout(() => {
      timer = void 0;
      void attempt();
    }, ms);
  };
  const attempt = async () => {
    const mine = epoch;
    const superseded = () => controller.signal.aborted || mine !== epoch;
    const attemptController = new AbortController();
    const cancel = () => attemptController.abort();
    controller.signal.addEventListener("abort", cancel, { once: true });
    inFlight = attemptController;
    try {
      if (superseded()) return;
      let landed = false;
      if (await ready()) {
        if (superseded()) return;
        const result = await loadEffectiveChains({
          fetchJson: options.fetchJson,
          url: options.url,
          timeoutMs,
          signal: attemptController.signal,
          appliedRevision
        });
        if (superseded()) return;
        if (result.ok) {
          landed = true;
          appliedRevision = result.revision;
          if (!loadedOnce) {
            loadedOnce = true;
            log.info("effective chain registry loaded", {
              revision: result.revision,
              publishedRevision: result.publishedRevision,
              added: result.hydration?.added.length ?? 0,
              replaced: result.hydration?.replaced.length ?? 0,
              removed: result.hydration?.removed.length ?? 0,
              rejected: result.hydration?.rejected.length ?? 0,
              attempts: failures + 1
            });
            settle(result);
          }
        }
      }
      if (landed || loadedOnce) {
        schedule(refreshMs);
      } else {
        schedule(failures < retryDelays.length ? retryDelays[failures] : refreshMs);
        failures += 1;
      }
    } catch (error) {
      log.warn("effective chain registry refresh failed", { error: error instanceof Error ? error.message : String(error) });
      if (!superseded()) schedule(refreshMs);
    } finally {
      controller.signal.removeEventListener("abort", cancel);
      if (inFlight === attemptController) inFlight = null;
    }
  };
  const supersede = () => {
    epoch += 1;
    inFlight?.abort();
    inFlight = null;
    if (timer !== void 0) clearTimeout(timer);
    timer = void 0;
  };
  const refresh = () => {
    if (controller.signal.aborted) return;
    supersede();
    schedule(0);
  };
  const resetIdentity = () => {
    if (controller.signal.aborted) return;
    supersede();
    appliedRevision = null;
    const dropped = dropPrivateNetworks();
    if (dropped.length > 0) log.info("private networks dropped on identity change", { dropped });
    schedule(refreshMs);
  };
  self = { loaded, stop, refresh, resetIdentity };
  if (!controller.signal.aborted) {
    running.add(self);
    schedule(0);
  }
  return self;
}

// src/chains/ingest-coverage.generated.json
var ingest_coverage_generated_default = {
  ingestable_chain_ids: [
    "aptos:1",
    "aptos:2",
    "bip122:000000000000000000651ef99cb9fcbe",
    "bip122:000000000019d6689c085ae165831e93",
    "bip122:000000000933ea01ad0ee984209779ba",
    "bip122:00040fe8ec8471911baa1db1266ea15d",
    "bip122:12a765e31ffd4059bada1e25190f6e98",
    "bip122:1a91e3dace36e2be3bf030a65679fe82",
    "cosmos:cosmoshub-4",
    "cosmos:dydx-mainnet-1",
    "cosmos:noble-1",
    "cosmos:osmosis-1",
    "cosmos:thorchain-1",
    "eip155:1",
    "eip155:10",
    "eip155:100",
    "eip155:11155111",
    "eip155:11155420",
    "eip155:1135",
    "eip155:1284",
    "eip155:1285",
    "eip155:130",
    "eip155:1328",
    "eip155:1329",
    "eip155:137",
    "eip155:143",
    "eip155:146",
    "eip155:167000",
    "eip155:1868",
    "eip155:2020",
    "eip155:2021",
    "eip155:25",
    "eip155:2741",
    "eip155:324",
    "eip155:33139",
    "eip155:34443",
    "eip155:369",
    "eip155:42161",
    "eip155:421614",
    "eip155:42220",
    "eip155:43113",
    "eip155:43114",
    "eip155:4326",
    "eip155:4663",
    "eip155:480",
    "eip155:5000",
    "eip155:534352",
    "eip155:545",
    "eip155:56",
    "eip155:57073",
    "eip155:59144",
    "eip155:747",
    "eip155:747474",
    "eip155:80002",
    "eip155:80094",
    "eip155:8453",
    "eip155:84532",
    "eip155:88888",
    "eip155:97",
    "eip155:9745",
    "eip155:998",
    "eip155:999",
    "hyperliq:mainnet",
    "hyperliq:testnet",
    "monero:418015bb9ae982a1975da7d79277c270",
    "near:mainnet",
    "near:testnet",
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
    "starknet:SN_MAIN",
    "sui:mainnet",
    "sui:testnet",
    "tron:2494104990",
    "tron:3448148188",
    "tron:728126428",
    "tvm:-239",
    "tvm:-3",
    "xrpl:0",
    "xrpl:1"
  ],
  provider_free_sources: [
    "inline"
  ],
  routes: [
    {
      chain_ids: [
        "sui:mainnet",
        "sui:testnet"
      ],
      family: "sui",
      provider: "sui_graphql",
      select: {
        provider: "sui_graphql"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "starknet:SN_MAIN"
      ],
      family: "starknet",
      provider: "starknet_rpc",
      select: {
        provider: "starknet_rpc"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "aptos:1",
        "aptos:2"
      ],
      family: "aptos",
      provider: "aptos_rest",
      select: {
        provider: "aptos_rest"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "tvm:-239",
        "tvm:-3"
      ],
      family: "ton",
      provider: "toncenter",
      select: {
        provider: "toncenter"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "near:mainnet",
        "near:testnet"
      ],
      family: "near",
      provider: "near_rpc",
      select: {
        provider: "near_rpc"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "cosmos:cosmoshub-4",
        "cosmos:dydx-mainnet-1",
        "cosmos:noble-1",
        "cosmos:osmosis-1",
        "cosmos:thorchain-1"
      ],
      family: "cosmos",
      provider: "cosmos_lcd",
      select: {
        provider: "cosmos_lcd"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "xrpl:0",
        "xrpl:1"
      ],
      family: "xrpl",
      provider: "xrpscan",
      select: {
        provider: "xrpscan"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "tron:2494104990",
        "tron:3448148188",
        "tron:728126428"
      ],
      family: "tron",
      provider: "tronscan",
      select: {
        provider: "tronscan"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "eip155:1",
        "eip155:10",
        "eip155:11155111",
        "eip155:1135",
        "eip155:1284",
        "eip155:1285",
        "eip155:1328",
        "eip155:1329",
        "eip155:137",
        "eip155:143",
        "eip155:2020",
        "eip155:2021",
        "eip155:25",
        "eip155:369",
        "eip155:42161",
        "eip155:43114",
        "eip155:545",
        "eip155:56",
        "eip155:59144",
        "eip155:747",
        "eip155:80002",
        "eip155:8453",
        "eip155:84532",
        "eip155:88888",
        "eip155:97"
      ],
      family: "evm",
      provider: "moralis",
      select: {
        not: [
          {
            provider: "evm_ingest",
            values: [
              "blockscout_v2"
            ]
          },
          {
            provider: "evm_rpc",
            values: [
              "ingest"
            ]
          }
        ],
        provider: "moralis"
      },
      source_kinds: [
        "block_range",
        "latest",
        "pair_swaps",
        "token_transfers",
        "tx_hashes",
        "tx_specs",
        "wallet_swaps"
      ]
    },
    {
      chain_ids: [
        "eip155:100",
        "eip155:146",
        "eip155:167000",
        "eip155:2741",
        "eip155:324",
        "eip155:4326",
        "eip155:5000",
        "eip155:534352",
        "eip155:747474",
        "eip155:9745",
        "eip155:998",
        "eip155:999"
      ],
      family: "evm",
      provider: "evm_rpc",
      select: {
        provider: "evm_rpc",
        values: [
          "ingest"
        ]
      },
      source_kinds: [
        "block_range",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "eip155:999"
      ],
      family: "evm",
      provider: "etherscan_history_rpc",
      select: {
        provider: "evm_history",
        values: [
          "etherscan_scan_rpc"
        ]
      },
      source_kinds: [
        "latest"
      ]
    },
    {
      chain_ids: [
        "eip155:130",
        "eip155:1868",
        "eip155:324",
        "eip155:33139",
        "eip155:34443",
        "eip155:42220",
        "eip155:4663",
        "eip155:480",
        "eip155:534352",
        "eip155:57073"
      ],
      family: "evm",
      provider: "blockscout",
      select: {
        not: [
          {
            provider: "moralis"
          }
        ],
        prefer: [
          {
            provider: "evm_ingest",
            values: [
              "blockscout_v2"
            ]
          }
        ],
        provider: "blockscout_v2"
      },
      source_kinds: [
        "block_range",
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "eip155:11155420",
        "eip155:421614",
        "eip155:43113",
        "eip155:80094"
      ],
      family: "evm",
      provider: "etherscan_family",
      select: {
        not: [
          {
            provider: "moralis"
          },
          {
            provider: "blockscout_v2"
          },
          {
            provider: "evm_rpc",
            values: [
              "ingest"
            ]
          }
        ],
        provider: "etherscan_v2"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "hyperliq:mainnet",
        "hyperliq:testnet"
      ],
      family: "hypercore",
      provider: "hyperliquid",
      select: {
        provider: "hyperliquid"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
        "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
        "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1"
      ],
      family: "solana",
      provider: "helius",
      select: {
        provider: "helius"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "bip122:000000000019d6689c085ae165831e93",
        "bip122:000000000933ea01ad0ee984209779ba"
      ],
      family: "utxo",
      provider: "esplora",
      select: {
        provider: "esplora"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "bip122:000000000000000000651ef99cb9fcbe",
        "bip122:00040fe8ec8471911baa1db1266ea15d",
        "bip122:12a765e31ffd4059bada1e25190f6e98",
        "bip122:1a91e3dace36e2be3bf030a65679fe82",
        "monero:418015bb9ae982a1975da7d79277c270"
      ],
      family: "utxo",
      provider: "xmrchain+blockchair_api",
      select: {
        or: [
          {
            provider: "blockchair_api"
          }
        ],
        provider: "xmrchain"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    }
  ],
  wallet_history_chain_ids: [
    "bip122:000000000019d6689c085ae165831e93",
    "bip122:000000000933ea01ad0ee984209779ba",
    "eip155:1",
    "eip155:10",
    "eip155:100",
    "eip155:11155111",
    "eip155:1135",
    "eip155:1284",
    "eip155:1285",
    "eip155:130",
    "eip155:1328",
    "eip155:1329",
    "eip155:137",
    "eip155:143",
    "eip155:1868",
    "eip155:2020",
    "eip155:2021",
    "eip155:25",
    "eip155:324",
    "eip155:33139",
    "eip155:34443",
    "eip155:369",
    "eip155:42161",
    "eip155:42220",
    "eip155:43114",
    "eip155:4663",
    "eip155:480",
    "eip155:534352",
    "eip155:545",
    "eip155:56",
    "eip155:57073",
    "eip155:59144",
    "eip155:747",
    "eip155:80002",
    "eip155:8453",
    "eip155:84532",
    "eip155:88888",
    "eip155:97",
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
    "tron:2494104990",
    "tron:3448148188",
    "tron:728126428",
    "xrpl:0",
    "xrpl:1"
  ]
};

// src/chains/coverage.ts
var BindingSchema = z.object({
  provider: z.string().min(1),
  values: z.array(z.string()).nullable().optional()
});
var SelectSchema = BindingSchema.extend({
  or: z.array(BindingSchema).optional(),
  not: z.array(BindingSchema).optional(),
  prefer: z.array(BindingSchema).optional()
});
var RouteSchema = z.object({
  family: z.string().default(""),
  provider: z.string().default(""),
  source_kinds: z.array(z.string()).default([]),
  chain_ids: z.array(z.string()).default([]),
  // Absent on a snapshot written before routes carried rules, in which case
  // `chain_ids` is the whole answer.
  select: SelectSchema.nullable().optional()
});
var CoverageSchema = z.object({
  wallet_history_chain_ids: z.array(z.string()).default([]),
  ingestable_chain_ids: z.array(z.string()).default([]),
  provider_free_sources: z.array(z.string()).default([]),
  routes: z.array(RouteSchema).default([])
});
var SNAPSHOT = CoverageSchema.parse(ingest_coverage_generated_default);
var WALLET_HISTORY_CHAIN_IDS = Object.freeze(SNAPSHOT.wallet_history_chain_ids);
function bindingOf(wire) {
  return Object.freeze({ provider: wire.provider, values: wire.values ? Object.freeze([...wire.values]) : null });
}
var RULES = Object.freeze(
  SNAPSHOT.routes.map(
    (route) => Object.freeze({
      family: route.family,
      provider: route.provider,
      sourceKinds: Object.freeze([...route.source_kinds]),
      generatedChainIds: Object.freeze([...route.chain_ids]),
      select: route.select ? Object.freeze({
        anyOf: Object.freeze([bindingOf(route.select), ...(route.select.or ?? []).map(bindingOf)]),
        noneOf: Object.freeze((route.select.not ?? []).map(bindingOf)),
        prefer: Object.freeze((route.select.prefer ?? []).map(bindingOf))
      }) : null
    })
  )
);
function bound(binding, chain) {
  const value = chain.providers[binding.provider];
  return value !== void 0 && (binding.values === null || binding.values.includes(value));
}
function serves(rule, chain) {
  if (!rule.select) return rule.generatedChainIds.includes(chain.caip2);
  if (!isActiveChain(chain)) return false;
  if (rule.family && chain.family !== rule.family) return false;
  if (!rule.select.anyOf.some((binding) => bound(binding, chain))) return false;
  if (rule.select.prefer.some((binding) => bound(binding, chain))) return true;
  return !rule.select.noneOf.some((binding) => bound(binding, chain));
}
var derived = globalSingleton("chains/coverage", () => ({
  version: -1,
  routes: [],
  ingestable: [],
  kindsByChain: /* @__PURE__ */ new Map(),
  subscribed: false
}));
function recompute() {
  const chains = registry.all();
  const known = new Set(chains.map((chain) => chain.caip2));
  const kinds = /* @__PURE__ */ new Map();
  const routes = [];
  for (const rule of RULES) {
    const chainIds = /* @__PURE__ */ new Set();
    for (const chain of chains) if (serves(rule, chain)) chainIds.add(chain.caip2);
    if (!rule.select) {
      for (const id of rule.generatedChainIds) if (!known.has(id)) chainIds.add(id);
    }
    const sorted = [...chainIds].sort();
    for (const id of sorted) {
      const set = kinds.get(id) ?? /* @__PURE__ */ new Set();
      for (const kind of rule.sourceKinds) set.add(kind);
      kinds.set(id, set);
    }
    routes.push(Object.freeze({
      family: rule.family,
      provider: rule.provider,
      sourceKinds: rule.sourceKinds,
      chainIds: Object.freeze(sorted),
      select: rule.select
    }));
  }
  derived.routes.splice(0, derived.routes.length, ...routes);
  derived.ingestable.splice(0, derived.ingestable.length, ...[...kinds.keys()].sort());
  derived.kindsByChain = kinds;
  derived.version = registry.version;
}
function current() {
  if (!derived.subscribed) {
    derived.subscribed = true;
    registry.subscribe(() => {
      if (derived.version !== registry.version) recompute();
    });
  }
  if (derived.version !== registry.version) recompute();
  return derived;
}
var INGEST_ROUTES = current().routes;
function ingestRoutes() {
  return current().routes;
}
var PROVIDER_FREE_SOURCES = Object.freeze(
  SNAPSHOT.provider_free_sources
);
var PROVIDER_FREE = new Set(PROVIDER_FREE_SOURCES);
var INGESTABLE_CHAIN_IDS = current().ingestable;
function ingestableChainIds() {
  return current().ingestable;
}
function canonicalOf(chainId) {
  return toCanonicalChainId(chainId) ?? chainId;
}
function kindsOf(chainId) {
  return current().kindsByChain.get(canonicalOf(chainId));
}
function ingestSourceKinds(chainId) {
  const kinds = new Set(PROVIDER_FREE_SOURCES);
  if (chainId) for (const kind of kindsOf(chainId) ?? []) kinds.add(kind);
  return [...kinds].sort();
}
function isIngestableChain(chainId, sourceKind) {
  if (!chainId) return false;
  if (isPrivateNetworkChain(chainId)) return !sourceKind || PROVIDER_FREE.has(sourceKind);
  const kinds = kindsOf(chainId);
  if (!sourceKind) return kinds !== void 0;
  return PROVIDER_FREE.has(sourceKind) || (kinds?.has(sourceKind) ?? false);
}
function uncoveredChains(chainIds, sourceKind) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const chainId of chainIds) {
    if (!chainId || isIngestableChain(chainId, sourceKind)) continue;
    const canonical = canonicalOf(chainId);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push(canonical);
  }
  return out;
}

// src/chains/options.ts
function valueFor(chain, kind) {
  switch (kind) {
    case "chainIdHex":
      return chain.chainIdHex;
    case "caip2":
      return chain.caip2;
    default:
      return chain.slug;
  }
}
function chainSelectOptions(config = {}) {
  const {
    valueKind = "slug",
    evmOnly = false,
    includeTestnets = false,
    sort = "name",
    fallback = "skip",
    chains,
    offer = "ingestable",
    includeInactive = false,
    keep
  } = config;
  const source = chains ?? registry.all();
  const kept = /* @__PURE__ */ new Set();
  for (const value of keep ?? []) {
    const chain = value ? getChainMetadata(value) : null;
    if (chain) kept.add(chain.caip2);
  }
  const out = [];
  for (const chain of source) {
    const active = isActiveChain(chain);
    if (!includeInactive && !active && !kept.has(chain.caip2)) continue;
    if (!chains && offer === "ingestable" && !isIngestableChain(chain.caip2) && !kept.has(chain.caip2)) continue;
    if (evmOnly && !chainIsEvm(chain)) continue;
    const owned = isPrivateNetwork(chain);
    if (!includeTestnets && chain.isTestnet && !owned) continue;
    let value = valueFor(chain, valueKind);
    if (value == null) {
      if (fallback === "slug") value = chain.slug;
      else continue;
    }
    out.push({
      value,
      label: chain.name,
      slug: chain.slug,
      isTestnet: chain.isTestnet,
      active,
      caip2: chain.caip2,
      ...owned ? { networkKind: chain.networkKind } : {}
    });
  }
  out.sort(
    (a, b) => sort === "ethFirst" && (a.slug === "eth" || b.slug === "eth") ? a.slug === "eth" ? -1 : 1 : a.label.localeCompare(b.label)
  );
  return out;
}
function chainFilterOptions(chainIds, config = {}) {
  const { countOf, noteOf, sort = "count" } = config;
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const id of chainIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const meta = getChainMetadata(id);
    const count = countOf?.(id);
    const note = count == null ? noteOf?.(id) : void 0;
    out.push({
      value: id,
      label: meta?.name ?? id,
      slug: meta?.slug ?? id,
      ...count != null ? { count } : {},
      ...note ? { note } : {}
    });
  }
  out.sort((a, b) => {
    if (sort === "count") {
      const delta = (b.count ?? 0) - (a.count ?? 0);
      if (delta !== 0) return delta;
    }
    return a.label.localeCompare(b.label);
  });
  return out;
}
function mergeSeenChains(previous, incoming, keep = []) {
  const seen = new Set(previous);
  const added = [];
  for (const id of [...incoming, ...keep]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    added.push(id);
  }
  return added.length === 0 ? previous : [...previous, ...added];
}

// src/chains/finality.ts
var DEFAULT_REORG_DEPTH = 12;
var FAMILY_DEFAULT_DEPTHS = {
  [ChainFamily.EVM]: 12,
  [ChainFamily.UTXO]: 6,
  [ChainFamily.SOLANA]: 32,
  [ChainFamily.COSMOS]: 1,
  [ChainFamily.HYPERCORE]: 1,
  [ChainFamily.OTHER]: DEFAULT_REORG_DEPTH
};
function familyDefaultDepth(family) {
  return FAMILY_DEFAULT_DEPTHS[family] ?? DEFAULT_REORG_DEPTH;
}
function asChain(chainOrId) {
  return typeof chainOrId === "string" ? registry.resolve(chainOrId) : chainOrId;
}
function familyFor(chainOrId) {
  return asChain(chainOrId)?.family ?? null;
}
function finalityDepthFor(chainOrId) {
  const chain = asChain(chainOrId);
  if (!chain) return DEFAULT_REORG_DEPTH;
  return chain.reorgDepth ?? familyDefaultDepth(chain.family);
}
function finalityDepthForFamily(family) {
  return familyDefaultDepth(family);
}
var DEFAULT_BLOCK_TIME_SECONDS = 12;
var FAMILY_BLOCK_TIME_SECONDS = {
  [ChainFamily.EVM]: 12,
  [ChainFamily.UTXO]: 600,
  // Bitcoin ~10 min target
  [ChainFamily.SOLANA]: 1,
  // ~400ms slots, rounded up
  [ChainFamily.COSMOS]: 6,
  [ChainFamily.HYPERCORE]: 1,
  // sub-second HyperBFT blocks, rounded up
  [ChainFamily.OTHER]: DEFAULT_BLOCK_TIME_SECONDS
};
function blockTimeSecondsFor(chainOrId) {
  const chain = asChain(chainOrId);
  if (!chain) return DEFAULT_BLOCK_TIME_SECONDS;
  return chain.blockTimeSec ?? FAMILY_BLOCK_TIME_SECONDS[chain.family] ?? DEFAULT_BLOCK_TIME_SECONDS;
}
function confirmedBlockForHead(head, chainOrId) {
  return Math.max(0, head - finalityDepthFor(chainOrId));
}
function isBlockFinalized(blockNumber, head, chainOrId) {
  return head - blockNumber >= finalityThresholdsFor(chainOrId).finalityBlocks;
}
var DEFAULT_CONFIRMATION_BLOCKS = 32;
var DEFAULT_FINALITY_BLOCKS = 256;
function finalityThresholdsFor(chainOrId) {
  const chain = asChain(chainOrId);
  const confirmationBlocks = chain ? chain.reorgDepth : DEFAULT_CONFIRMATION_BLOCKS;
  const declared = chain?.finalityBlocks ?? void 0;
  return {
    confirmationBlocks,
    finalityBlocks: Math.max(declared ?? DEFAULT_FINALITY_BLOCKS, confirmationBlocks)
  };
}
var FinalityState = {
  UNKNOWN: "unknown",
  MEMPOOL: "mempool",
  PENDING: "pending",
  OBSERVED: "observed",
  CONFIRMED: "confirmed",
  FINALIZED: "finalized",
  REORGED: "reorged"
};
var FINALITY_VALUES = new Set(Object.values(FinalityState));
function coerceFinality(value) {
  const v = String(value ?? "").toLowerCase();
  return FINALITY_VALUES.has(v) ? v : null;
}
var FINALITY_RANK = {
  [FinalityState.UNKNOWN]: -1,
  [FinalityState.MEMPOOL]: 0,
  [FinalityState.PENDING]: 0,
  [FinalityState.OBSERVED]: 0,
  [FinalityState.REORGED]: 0,
  [FinalityState.CONFIRMED]: 1,
  [FinalityState.FINALIZED]: 2
};
function finalityRank(state) {
  return FINALITY_RANK[state] ?? -1;
}

// src/chains/reorg.ts
function confirmationsFor(blockNumber, head) {
  return Math.max(0, head - blockNumber + 1);
}
function blocksUntilFinal(blockNumber, head, chainOrId) {
  const { finalityBlocks } = finalityThresholdsFor(chainOrId);
  return Math.max(0, finalityBlocks - (head - blockNumber));
}
function confirmationsUntilConfirmed(blockNumber, head, chainOrId) {
  const { confirmationBlocks } = finalityThresholdsFor(chainOrId);
  return Math.max(0, confirmationBlocks - (head - blockNumber));
}
function computeFinalityState(input) {
  if (input.reorged) return FinalityState.REORGED;
  if (familyFor(input.chainOrId) === ChainFamily.TON) return FinalityState.OBSERVED;
  if (input.blockNumber == null || input.head == null) return FinalityState.OBSERVED;
  const depth = input.head - input.blockNumber;
  if (depth < 0) return FinalityState.OBSERVED;
  const { confirmationBlocks, finalityBlocks } = finalityThresholdsFor(input.chainOrId);
  if (depth >= finalityBlocks) return FinalityState.FINALIZED;
  if (depth >= confirmationBlocks) return FinalityState.CONFIRMED;
  return FinalityState.OBSERVED;
}
function finalityFromAge(input) {
  if (familyFor(input.chainOrId) === ChainFamily.TON) return FinalityState.OBSERVED;
  const { blockTimestamp } = input;
  if (blockTimestamp == null || blockTimestamp <= 0) return FinalityState.OBSERVED;
  const blockTime = blockTimeSecondsFor(input.chainOrId);
  if (blockTime <= 0) return FinalityState.OBSERVED;
  const ageSec = (input.now ?? Date.now()) / 1e3 - blockTimestamp;
  if (ageSec <= 0) return FinalityState.OBSERVED;
  const { confirmationBlocks, finalityBlocks } = finalityThresholdsFor(input.chainOrId);
  if (ageSec >= finalityBlocks * blockTime) return FinalityState.FINALIZED;
  if (ageSec >= confirmationBlocks * blockTime) return FinalityState.CONFIRMED;
  return FinalityState.OBSERVED;
}
function resolveFinality(input) {
  const row = coerceFinality(input.rowFinality) ?? FinalityState.UNKNOWN;
  if (row === FinalityState.REORGED || input.reorged) return FinalityState.REORGED;
  const computed = computeFinalityState(input);
  if (computed === FinalityState.REORGED) return FinalityState.REORGED;
  const best = finalityRank(computed) > finalityRank(row) ? computed : row;
  const byAge = finalityFromAge({ ...input, chainOrId: input.chainOrId });
  return finalityRank(byAge) > finalityRank(best) ? byAge : best;
}
function confirmationProgress(state, input) {
  if (familyFor(input.chainOrId) === ChainFamily.TON) return null;
  const { blockNumber, head } = input;
  if (blockNumber == null || head == null) return null;
  const thresholds = finalityThresholdsFor(input.chainOrId);
  const required = state === FinalityState.OBSERVED ? thresholds.confirmationBlocks : state === FinalityState.CONFIRMED ? thresholds.finalityBlocks : null;
  if (required == null) return null;
  return { current: Math.max(0, head - blockNumber), required };
}
function finalityProgress(input) {
  const state = resolveFinality(input);
  return { state, confirmations: confirmationProgress(state, input) };
}
var ReorgTracker = class {
  static {
    brandClass(this, "chains/ReorgTracker");
  }
  seen = /* @__PURE__ */ new Map();
  chainMap(chainId) {
    let m = this.seen.get(chainId);
    if (!m) {
      m = /* @__PURE__ */ new Map();
      this.seen.set(chainId, m);
    }
    return m;
  }
  /** Observe a block; returns a `ReorgEvent` if its hash changed at that height. */
  observe(chainId, block) {
    const m = this.chainMap(chainId);
    const prev = m.get(block.number);
    m.set(block.number, block.hash);
    if (prev && prev !== block.hash) {
      return {
        chainId,
        height: block.number,
        previousHash: prev,
        newHash: block.hash,
        reorgDepthFrom: block.number
      };
    }
    return null;
  }
  /** True if `blockNumber` falls within the replay window of a reorg at `event`. */
  isInReplayWindow(event, blockNumber, chainOrId) {
    const depth = finalityDepthFor(chainOrId);
    return blockNumber >= event.reorgDepthFrom - depth;
  }
  reset(chainId) {
    if (chainId) this.seen.delete(chainId);
    else this.seen.clear();
  }
};

// src/chains/platform-links.ts
var PLATFORM_APP = "dashboard";
function platformTransactionRef(hash, chain) {
  const value = (hash ?? "").trim();
  if (!value) return "";
  if (value.includes(":") || !value.toLowerCase().startsWith("0x")) return value;
  const chainToken = (chain ?? "").trim();
  return chainToken ? `${chainToken}:${value}` : value;
}
function platformTransactionPath(hash, chain) {
  const ref = platformTransactionRef(hash, chain);
  return ref ? `/transactions/${encodeURIComponent(ref)}` : void 0;
}
function platformAddressPath(address, chain) {
  const value = (address ?? "").trim();
  if (!value) return void 0;
  const chainToken = (chain ?? "").trim();
  const path = `/addresses/${encodeURIComponent(value)}`;
  return chainToken ? `${path}?chain=${encodeURIComponent(chainToken)}` : path;
}
function platformTransactionUrl(hash, chain, ctx) {
  const path = platformTransactionPath(hash, chain);
  return path ? resolveAppUrl({ application: PLATFORM_APP, path }, ctx) : void 0;
}
function platformAddressUrl(address, chain, ctx) {
  const path = platformAddressPath(address, chain);
  return path ? resolveAppUrl({ application: PLATFORM_APP, path }, ctx) : void 0;
}

// src/chains/monogram.ts
var cache = globalSingleton("chains/monogram", () => ({
  version: -1,
  use: /* @__PURE__ */ new Map()
}));
function tickerUse() {
  if (cache.version === registry.version) return cache.use;
  const use = /* @__PURE__ */ new Map();
  for (const chain of registry.all()) {
    if (chain.isTestnet) continue;
    const ticker = chain.nativeToken?.trim().toUpperCase();
    if (ticker) use.set(ticker, (use.get(ticker) ?? 0) + 1);
  }
  cache.use = use;
  cache.version = registry.version;
  return use;
}
function fromName(name) {
  const firstWord = name.trim().split(/\s+/)[0] ?? "";
  const letters = firstWord.replace(/[^0-9a-z]/gi, "");
  return letters ? letters.slice(0, 3).toUpperCase() : null;
}
function isMachineSlug(chain) {
  const slug = chain.slug.trim().toLowerCase();
  if (/^[a-z0-9]+-\d+$/.test(slug)) return true;
  const [namespace, reference] = chain.caip2.trim().toLowerCase().split(":");
  return !!namespace && !!reference && /\d/.test(reference) && slug === `${namespace}-${reference}`;
}
var GENERIC_NAME_WORDS = /* @__PURE__ */ new Set([
  "mainnet",
  "testnet",
  "devnet",
  "network",
  "chain",
  "blockchain",
  "evm",
  "deprecated",
  "legacy"
]);
function fromImportedName(name) {
  const words = name.split(/[\s\-_/()[\].,:]+/).map((word) => word.replace(/[^0-9a-z]/gi, "")).filter(Boolean);
  const telling = words.filter((word) => !GENERIC_NAME_WORDS.has(word.toLowerCase()));
  const use = telling.length > 0 ? telling : words;
  const first = use[0];
  if (!first) return null;
  const initials = use.length > 1 ? use.slice(0, 3).map((word) => word[0]).join("").toUpperCase() : null;
  if (first.length === 1 && initials) return { letters: initials, misread: false, initials };
  const letters = first.slice(0, 3).toUpperCase();
  const misread = letters.length < first.length && (tickerUse().get(letters) ?? 0) > 1;
  return { letters, misread, initials };
}
function chainMonogram(chain, fallback) {
  const plain = fallback.slice(0, 3).toUpperCase();
  if (!chain) return plain;
  const ticker = chain.nativeToken?.trim().toUpperCase() ?? "";
  if (isMachineSlug(chain)) {
    const named = fromImportedName(chain.name);
    if (!named) return ticker.slice(0, 3) || plain;
    if (!named.misread) return named.letters;
    if (ticker && (tickerUse().get(ticker) ?? 0) <= 1) return ticker.slice(0, 3);
    return named.initials ?? named.letters;
  }
  if (ticker) {
    const shared = (tickerUse().get(ticker) ?? 0) > 1;
    const itsOwn = !chain.isTestnet && (chain.slug.toUpperCase() === ticker || chain.aliases.some((a) => a.toUpperCase() === ticker));
    if (!shared || itsOwn) return ticker.slice(0, 3);
  }
  return fromName(chain.name) ?? (ticker.slice(0, 3) || plain);
}

export { DEFAULT_BLOCK_TIME_SECONDS, DEFAULT_CONFIRMATION_BLOCKS, DEFAULT_FINALITY_BLOCKS, DEFAULT_REORG_DEPTH, EFFECTIVE_CHAINS_BACKGROUND_TIMEOUT_MS, EFFECTIVE_CHAINS_REFRESH_MS, EFFECTIVE_CHAINS_RETRY_DELAYS_MS, EFFECTIVE_CHAINS_TIMEOUT_MS, EffectiveChainsResponseSchema, FinalityState, INGESTABLE_CHAIN_IDS, INGEST_ROUTES, PLATFORM_APP, PROTOCOL_FACT_LINKS, PROVIDER_FREE_SOURCES, ReorgTracker, WALLET_HISTORY_CHAIN_IDS, blockTimeSecondsFor, blocksUntilFinal, chainFilterOptions, chainMonogram, chainSelectOptions, coerceFinality, computeFinalityState, confirmationProgress, confirmationsFor, confirmationsUntilConfirmed, confirmedBlockForHead, factLinkHref, factLinkMatches, familyDefaultDepth, familyFor, finalityDepthFor, finalityDepthForFamily, finalityFromAge, finalityProgress, finalityRank, finalityThresholdsFor, getFactLink, getFactLinkUrl, ingestRoutes, ingestSourceKinds, ingestableChainIds, isBlockFinalized, isIngestableChain, loadEffectiveChains, mergeSeenChains, platformAddressPath, platformAddressUrl, platformTransactionPath, platformTransactionRef, platformTransactionUrl, refreshEffectiveChains, rejectedFactLinks, resolveFinality, startEffectiveChainsHydration, uncoveredChains };
//# sourceMappingURL=chunk-ZRZ3KIAM.js.map
//# sourceMappingURL=chunk-ZRZ3KIAM.js.map